/**
 * pratica-status-client.ts — F-005 Phase 4 (IMPL-P4-002).
 *
 * Typed `fetch` wrappers for the 3 F-005 stato/correzione endpoints:
 *  - GET   /api/v1/pratiche?page=N           → PraticaListResponse
 *  - GET   /api/v1/pratiche/:id/status       → PraticaStatusDetail
 *  - PATCH /api/v1/pratiche/:id/correggi     → CorreggiResponse
 *
 * Conventions mirror `apps/web/src/features/pratica/lib/pratica-client.ts` and
 * `apps/web/src/features/archivio/lib/archivio-client.ts`:
 *  - Relative `/api/v1` base path (Vite dev proxy / same-origin in prod) — the
 *    codebase does NOT use `import.meta.env.VITE_API_URL` for these clients.
 *  - `credentials: 'include'` so the Better Auth session cookie travels along.
 *  - A typed error (`PraticaStatusApiError`) carrying `code` + `status`, lifted
 *    from the API JSON envelope (`{ code, message }`).
 */
import type {
  PraticaListResponse,
  PraticaStatusDetail,
  CorreggiRequest,
  CorreggiResponse,
} from '@app/shared';

const API = '/api/v1/pratiche';

export class PraticaStatusApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'PraticaStatusApiError';
  }
}

export function isPraticaStatusApiError(err: unknown): err is PraticaStatusApiError {
  return err instanceof PraticaStatusApiError;
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ code: 'UNKNOWN_ERROR' }))) as {
      code?: string;
      message?: string;
    };
    throw new PraticaStatusApiError(body.code ?? 'UNKNOWN_ERROR', res.status, body.message);
  }
  return res.json() as Promise<T>;
}

export const praticaStatusClient = {
  async listAttive(page = 1): Promise<PraticaListResponse> {
    const res = await fetch(`${API}?page=${page}`, {
      credentials: 'include',
    });
    return handleResponse<PraticaListResponse>(res);
  },

  async getStatus(praticaId: string): Promise<PraticaStatusDetail> {
    const res = await fetch(`${API}/${praticaId}/status`, {
      credentials: 'include',
    });
    return handleResponse<PraticaStatusDetail>(res);
  },

  async correggi(praticaId: string, body: CorreggiRequest): Promise<CorreggiResponse> {
    const res = await fetch(`${API}/${praticaId}/correggi`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return handleResponse<CorreggiResponse>(res);
  },
};
